import type {HTMLAttributes, ReactNode} from 'react';
import type {VariantProps} from 'class-variance-authority';
import {cva} from 'class-variance-authority';
import clsx from 'clsx';
import {twMerge} from 'tailwind-merge';

import {ErrorCircleIcon} from '@/modules/foundation/components/icons/error-circle-icon';
import {InfoCircleIcon} from '@/modules/foundation/components/icons/info-circle-icon';
import {SuccessCircleIcon} from '@/modules/foundation/components/icons/success-circle-icon';
import {WarningIcon} from '@/modules/foundation/components/icons/warning-icon';
import {Heading} from '@/modules/foundation/components/typography/heading';

const alert = cva(
	[
		'rounded-lg',
		'shrink-0',
		'p-4',
		'md:p-8',
		'flex',
		'gap-4',
		'md:gap-8',
		'text-primary-2',
	],
	{
		variants: {
			severity: {
				info: ['bg-primary-90'],
				success: ['bg-success-90'],
				warning: ['bg-warning-90'],
				error: ['bg-error-90'],
			},
		},
		defaultVariants: {
			severity: 'info',
		},
	},
);

export type AlertProps = VariantProps<typeof alert> &
	HTMLAttributes<HTMLDivElement> & {
		title?: string;
		children?: ReactNode;
	};

export const Alert = ({
	children,
	severity,
	title,
	className,
	...rest
}: AlertProps) => {
	let Icon = (
		<InfoCircleIcon
			aria-hidden
			className={clsx('text-3xl', 'text-primary-15')}
		/>
	);

	switch (severity) {
		case 'info':
			Icon = (
				<InfoCircleIcon
					aria-hidden
					className={clsx('text-3xl', 'text-primary-15')}
				/>
			);
			break;
		case 'success':
			Icon = (
				<SuccessCircleIcon
					aria-hidden
					className={clsx('text-3xl', 'text-success-15')}
				/>
			);
			break;
		case 'warning':
			Icon = (
				<WarningIcon
					aria-hidden
					className={clsx('text-3xl', 'text-warning-15')}
				/>
			);
			break;
		case 'error':
			Icon = (
				<ErrorCircleIcon
					aria-hidden
					className={clsx('text-3xl', 'text-error-15')}
				/>
			);
			break;
	}

	return (
		<div className={twMerge(alert({severity}), className)} {...rest}>
			<span>{Icon}</span>
			<div className={clsx('relative', 'mt-1')}>
				{title && (
					<Heading asChild size="md">
						<strong
							className={clsx(children && 'mb-2', 'inline-block')}
						>
							{title}
						</strong>
					</Heading>
				)}
				{children && <div>{children}</div>}
			</div>
		</div>
	);
};
